/**
 * AEO/GEO 최적화를 위한 JSON-LD 스키마 빌더 유틸리티
 * - AI 검색엔진(ChatGPT, Perplexity, Google AI Overview)이 인용하기 좋은 자연어 description 생성
 * - schema.org 스펙 준수
 */

const FALLBACK_IMAGE = 'https://cdn.daisomall.co.kr/file/CM/daisologo_1.91-1.png'
const BASE_URL = 'https://www.daisomall.co.kr'

/**
 * 지정 한도 내에서 문장부호/공백 경계로 잘라 단어 단절을 막고, 잘렸으면 '…' 부착.
 * (AI 인용 가독성 향상)
 */
function trimToBoundary (text, max) {
  if (text.length <= max) { return text }
  const slice = text.substring(0, max)
  let i = Math.max(slice.lastIndexOf('.'), slice.lastIndexOf('!'), slice.lastIndexOf('?'), slice.lastIndexOf('·'))
  if (i < max * 0.6) { i = slice.lastIndexOf(' ') } // 문장부호가 너무 일찍 나오면 공백 경계
  if (i < max * 0.6) { i = max } // 공백도 부족하면 하드컷
  return slice.substring(0, i).replace(/[\s·]+$/, '') + '…'
}

/**
 * 상품 자연어 description 생성
 * @param {Object} pd - 상품 데이터
 * @param {string} pd.name - 상품명
 * @param {string} pd.brand - 브랜드명
 * @param {string} pd.category - 카테고리명
 * @param {number|string} pd.price - 가격
 * @param {string} [pd.detailText] - 상세설명 텍스트 (HTML 제거 후)
 * @param {Object} [pd.rating] - 리뷰 { avg, count }
 * @param {string} [pd.availability] - 재고 상태 ('InStock' | 'OutOfStock')
 * @returns {string}
 */
export function buildProductDescription (pd) {
  const { name, brand, category, price, detailText, rating, availability } = pd

  // 상세설명이 있으면 우선 사용 (GEO 인용률 향상)
  if (detailText && detailText.trim().length > 0) {
    // 다이소 상세페이지 구조: [상품명 → 스펙/치수/소재 → 주의사항 → 품질표시]
    // '주의사항/품질표시/반품' 등 비인용(경고·법적) 구간에서 커트하여
    // 경고문 도배를 막고 AI 인용가치가 높은 핵심 스펙(치수·소재 등)만 남긴다.
    const CUT = /주의\s*사항|취급\s*시\s*주의|사용\s*(?:전|시)\s*주의|안전\s*사항|경\s*고|품질\s*표시|반품\s*및\s*교환|품질\s*보증|제품\s*보증/i
    const before = detailText.split(CUT)[0].replace(/\s+/g, ' ').trim()

    if (before.length >= 10) {
      // 180자 한도 내에서 문장부호/공백 경계로 잘라 단어 단절 방지, 잘렸으면 '…'
      let desc = before.length > 180 ? trimToBoundary(before, 180) : before
      const extras = []
      if (price !== '' && price != null) {
        const num = typeof price === 'number' ? price : Number(price)
        if (!isNaN(num)) { extras.push(`가격 ${num.toLocaleString()}원`) }
      }
      if (rating && rating.count > 0) {
        extras.push(`리뷰 ${rating.avg}점(${rating.count}건)`)
      }
      if (availability === 'OutOfStock') {
        extras.push('품절')
      }
      if (extras.length > 0) {
        desc += ` [${extras.join(', ')}]`
      }
      return desc
    }
    // 스펙 구간이 너무 짧으면(유효 정보 없음) 아래 자연어 폴백 사용
  }

  // 자연어형 description 조합 (폴백)
  const parts = []
  if (name) {
    const brandPart = brand && brand !== '다이소' ? `${brand}의 ` : '다이소 '
    const categoryPart = category ? `${category} 카테고리의 상품` : '상품'
    parts.push(`${brandPart}${name}은(는) ${categoryPart}입니다.`)
  }
  if (price) {
    const priceStr = typeof price === 'number' ? price.toLocaleString() : Number(price).toLocaleString()
    parts.push(`가격은 ${priceStr}원이며, 다이소몰에서 온라인 구매 및 매장픽업이 가능합니다.`)
  }
  if (rating && rating.count > 0) {
    parts.push(`리뷰 평점 ${rating.avg}점(${rating.count}건).`)
  }
  if (availability === 'OutOfStock') {
    parts.push('현재 품절 상품입니다.')
  }
  if (parts.length > 0) {
    parts.push('다이소의 균일가 상품으로 합리적인 가격에 만나보세요.')
  }

  return parts.join(' ') || '다이소몰에서 다양한 상품을 합리적인 가격에 만나보세요.'
}

/**
 * 카테고리 자연어 description 생성
 * @param {Object} params
 * @param {string} params.categoryName - 카테고리명
 * @param {string} [params.categoryPath] - 카테고리 경로 (대 > 중 > 소)
 * @returns {string}
 */
export function buildCategoryDescription ({ categoryName, categoryPath }) {
  if (!categoryName) {
    return '다이소몰에서 다양한 카테고리의 상품을 합리적인 가격에 만나보세요.'
  }
  const pathPart = categoryPath ? ` ${categoryPath} 카테고리의` : ''
  return `다이소몰 ${categoryName} 카테고리에서${pathPart} 다양한 상품을 합리적인 가격에 만나보세요. 생활용품 전문 온라인몰 다이소에서 필요한 상품을 찾아보세요.`
}

/**
 * 검색결과 자연어 description 생성
 * @param {Object} params
 * @param {string} params.searchTerm - 검색어
 * @returns {string}
 */
export function buildSearchDescription ({ searchTerm }) {
  if (!searchTerm) {
    return '다이소몰에서 원하는 상품을 검색해보세요.'
  }
  return `다이소몰에서 ${searchTerm} 검색 결과를 확인하세요. 다이소의 다양한 상품을 합리적인 가격에 만나보실 수 있습니다.`
}

/**
 * 홈 자연어 description 생성
 * @returns {string}
 */
export function buildHomeDescription () {
  return '다이소몰은 생활용품, 주방, 인테리어, 문구, 뷰티, 식품 등 다양한 카테고리의 상품을 합리적인 가격에 제공하는 온라인 쇼핑몰입니다. 균일가 생활용품 전문점 다이소의 공식 온라인몰에서 쇼핑하세요.'
}

/**
 * Product JSON-LD 스키마 생성
 * @param {Object} params
 * @param {string} params.name - 상품명
 * @param {string|string[]} [params.image] - 이미지 URL (단일 또는 배열)
 * @param {string} [params.description] - 상품 설명
 * @param {string} params.sku - 상품번호
 * @param {string} [params.brand] - 브랜드명
 * @param {string} [params.manufacturer] - 제조사명
 * @param {string} params.url - canonical URL
 * @param {number|string} [params.price] - 가격
 * @param {string} [params.availability] - 재고 상태 ('InStock' | 'OutOfStock')
 * @param {string} [params.category] - 카테고리명
 * @param {Object} [params.rating] - 리뷰 평점 { avg, count }
 * @param {Array<{name: string, value: string}>} [params.attributes] - 고시정보 (additionalProperty)
 * @returns {Object}
 */
export function buildProductJsonLd ({ name, image, description, sku, brand, manufacturer, url, price, availability, category, rating, attributes }) {
  // image 배열화 (schema.org Product.image 권장: 배열)
  const imageArr = image ? (Array.isArray(image) ? image : [image]) : [FALLBACK_IMAGE]

  // 가격 가드 (빈/null/NaN/음수 → price 키 생략, Offer 자체는 유지)
  const numericPrice = Number(price)
  const hasPrice = price !== '' && price != null && !isNaN(numericPrice) && numericPrice >= 0

  // 고시정보 → additionalProperty 변환 (빈 값 필터)
  const additionalProperty = (Array.isArray(attributes))
    ? attributes
      .filter(a => a && a.name && a.value !== undefined && a.value !== null && a.value !== '')
      .map(a => ({ '@type': 'PropertyValue', name: a.name, value: String(a.value) }))
    : null

  const product = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name,
    image: imageArr,
    brand: { '@type': 'Brand', name: brand || '다이소' },
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: 'KRW',
      itemCondition: 'https://schema.org/NewCondition',
      availability: `https://schema.org/${availability || 'OutOfStock'}`
    }
  }

  // 선택 필드 (값 있을 때만)
  if (description) { product.description = description }
  if (sku) { product.sku = sku }
  if (hasPrice) { product.offers.price = numericPrice }
  if (manufacturer) {
    product.manufacturer = { '@type': 'Organization', name: manufacturer }
  }
  if (category) { product.category = category }
  if (rating && rating.count > 0) {
    product.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: rating.avg,
      reviewCount: rating.count
    }
  }
  if (additionalProperty && additionalProperty.length > 0) {
    product.additionalProperty = additionalProperty
  }

  return product
}

/**
 * BreadcrumbList JSON-LD 스키마 생성
 * @param {Array<{name: string, url: string}>} items
 * @returns {Object}
 */
export function buildBreadcrumbJsonLd (items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url
    }))
  }
}

/**
 * SearchResultsPage JSON-LD 스키마 생성
 * @param {Object} params
 * @param {string} params.searchTerm - 검색어
 * @param {string} params.url - 페이지 URL
 * @returns {Object}
 */
export function buildSearchResultsJsonLd ({ searchTerm, url }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SearchResultsPage',
    name: `${searchTerm} 검색결과 - 다이소몰`,
    url,
    mainEntity: {
      '@type': 'WebSite',
      name: '다이소몰',
      url: BASE_URL
    }
  }
}

/**
 * FAQPage JSON-LD 스키마 생성
 * @param {Array<{question: string, answer: string}>} items - FAQ 항목 배열
 * @returns {Object}
 */
export function buildFAQPageJsonLd (items) {
  if (!items || items.length === 0) { return null }
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  }
}

export { FALLBACK_IMAGE, BASE_URL }
